"use client";

import { type FormSearchProfileBlock as FormSearchProfileType, type FormElement as FormVariantT, type MsFilterSet } from "@/payload-types";
import { RenderFilters, type FilterBlockSlug, type SelectedFilters } from "@/lib/blocks/render-filters";
import type { SearchProfileConfig } from "@/lib/marketsuite/search-profile-config";
import { buildRangeFilterDefaults } from "@/lib/marketsuite/filters/default-values";
import { useFilterData } from "@/lib/marketsuite/filters/use-filter-data";
import type { FilterBlock, PropertyFilterData } from "@/lib/types/marketsuite/datatypes/filters";
import { PROPERTY_TYPE_OPTIONS, type PropertyType } from "@/lib/types/marketsuite/property-type";
import { ToggleGroup, Toggle } from "@/components/frontend/ui/toggle-group";
import { useForm } from "@tanstack/react-form-nextjs";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo } from "react";

export type SearchProfileFieldValue = {
	propertyType: string;
	rules: SelectedFilters;
};

function parseSearchProfileFieldValue(raw: unknown): SearchProfileFieldValue {
	if (raw && typeof raw === "object" && !Array.isArray(raw)) {
		const o = raw as Record<string, unknown>;
		const propertyType = typeof o.propertyType === "string" ? o.propertyType : "";
		const rulesRaw = o.rules;
		const rules =
			rulesRaw && typeof rulesRaw === "object" && !Array.isArray(rulesRaw) ? ({ ...(rulesRaw as SelectedFilters) } as SelectedFilters) : {};
		return { propertyType, rules };
	}
	if (typeof raw === "string" && raw.length > 0) {
		return { propertyType: raw, rules: {} };
	}
	return { propertyType: "", rules: {} };
}

type SearchProfileRulesFormProps = {
	filters: FilterBlock[];
	activeRules: SelectedFilters;
	filterData: PropertyFilterData | null | undefined;
	variant?: FormVariantT | "default" | number | null | undefined;
	onRulesChange: (rules: SelectedFilters) => void;
};

function SearchProfileRulesForm({ filters, activeRules, filterData, variant, onRulesChange }: SearchProfileRulesFormProps) {
	const filterset = useMemo(() => ({ filters }) as Pick<MsFilterSet, "filters">, [filters]);
	const form = useForm({ defaultValues: activeRules });

	return (
		<RenderFilters
			filterset={filterset}
			form={form}
			selectedFilters={activeRules}
			filterData={filterData ?? undefined}
			formVariant={variant}
			onValueChange={(value, blockType) => onRulesChange({ ...activeRules, [blockType]: value })}
		/>
	);
}

type FormSearchProfileBlockProps = FormSearchProfileType & {
	containerClassName?: string;
	variant?: FormVariantT | "default" | number | null | undefined;
	fieldValue?: unknown;
	fieldError?: string;
	onFieldChange?: (value: unknown) => void;
	onFieldBlur?: () => void;
	searchProfileConfig?: SearchProfileConfig;
};

export const FormSearchProfileBlock: React.FC<FormSearchProfileBlockProps> = ({
	containerClassName,
	style,
	variant,
	fieldValue,
	fieldError,
	onFieldChange,
	onFieldBlur,
	searchProfileConfig,
}) => {
	const t = useTranslations("ms");
	const { propertyType: storedType, rules } = useMemo(() => parseSearchProfileFieldValue(fieldValue), [fieldValue]);

	const availableTypes = useMemo(() => {
		if (searchProfileConfig === undefined) {
			return PROPERTY_TYPE_OPTIONS.map((o) => o.value) as PropertyType[];
		}
		const enabled = new Set(Object.keys(searchProfileConfig) as PropertyType[]);
		return PROPERTY_TYPE_OPTIONS.map((o) => o.value).filter((v): v is PropertyType => enabled.has(v));
	}, [searchProfileConfig]);

	const selectedType = (storedType && availableTypes.includes(storedType as PropertyType) ? storedType : availableTypes[0]) ?? "";

	const filtersForType = useMemo(() => {
		if (!selectedType || !searchProfileConfig) return [];
		return searchProfileConfig[selectedType as PropertyType]?.filters ?? [];
	}, [searchProfileConfig, selectedType]);

	const filterData = useFilterData(filtersForType);
	const rangeDefaults = useMemo(() => buildRangeFilterDefaults(filtersForType), [filtersForType]);
	const activeRules = useMemo(() => ({ ...rangeDefaults, ...rules }), [rangeDefaults, rules]);

	const rulesFormKey = useMemo(
		() => `${selectedType}-${filtersForType.map((block) => block.id ?? block.blockType).join(",")}`,
		[selectedType, filtersForType],
	);

	const setWholeValue = useCallback(
		(next: SearchProfileFieldValue) => {
			onFieldChange?.(next);
		},
		[onFieldChange],
	);

	const handleRulesChange = useCallback(
		(nextRules: SelectedFilters) => {
			setWholeValue({ propertyType: selectedType, rules: nextRules });
		},
		[selectedType, setWholeValue],
	);

	useEffect(() => {
		if (!selectedType) return;

		const needsPropertyType = storedType !== selectedType;
		const needsRangeDefaults = Object.keys(rangeDefaults).some((key) => rules[key as FilterBlockSlug] === undefined);

		if (!needsPropertyType && !needsRangeDefaults) return;

		setWholeValue({ propertyType: selectedType, rules: activeRules });
	}, [selectedType, storedType, rangeDefaults, rules, activeRules, setWholeValue]);

	const showPropertyTypeSelect = availableTypes.length > 1;

	return (
		<div className={containerClassName} onBlur={onFieldBlur}>
			{showPropertyTypeSelect ? (
				<ToggleGroup
					variant={style?.toggleGroupVariant ?? "default"}
					value={selectedType ? [selectedType] : []}
					multiple={false}
					onValueChange={(values) => {
						const next = values[0];
						if (next) setWholeValue({ propertyType: next, rules: {} });
					}}
				>
					{availableTypes.map((type) => (
						<Toggle key={type} value={type}>
							{t(`property-types.${type}`)}
						</Toggle>
					))}
				</ToggleGroup>
			) : null}

			{filtersForType.length > 0 ? (
				<div className={showPropertyTypeSelect ? "mt-4" : undefined}>
					<SearchProfileRulesForm
						key={rulesFormKey}
						filters={filtersForType}
						activeRules={activeRules}
						filterData={filterData}
						variant={variant}
						onRulesChange={handleRulesChange}
					/>
				</div>
			) : null}

			{fieldError && (
				<div className="mt-1 inline-flex w-full items-center border px-3 py-1.5 text-xs font-medium rounded-(--ms-form-el-border-radius,var(--radius-md)) border-red-200 bg-red-50 text-red-700">
					{fieldError}
				</div>
			)}
		</div>
	);
};
